import { Component, OnInit } from '@angular/core';

import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import { AlertService, MessageSeverity, DialogType } from '../../../services/alert.service';
import { User } from '../../../models/user.model';
import { Role } from '../../../models/role.model';
import { PasswordPolicyAssignment } from '../../../models/password-policy-assignment.model';
import Confirm = DialogType.Confirm;

@Component({
  selector: 'password-policy-management',
  templateUrl: './password-policy-management.component.html',
  styleUrls: ['./password-policy-management.component.css']
})
export class PasswordPolicyManagementComponent implements OnInit {

  isLoading = false;
  isSaving = false;

  allUsers: User[] = [];
  allRoles: Role[] = [];
  assignments: PasswordPolicyAssignment[] = [];

  selectedUserIds: { [userId: string]: boolean } = {};
  selectedRoleIds: { [roleId: string]: boolean } = {};
  targetAllUsers = false;

  requiredInDays = 14;
  repeatIntervalDays = 90;

  constructor(private accountService: AccountService, private authService: AuthService, private alertService: AlertService) {
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isLoading = true;
    this.alertService.startLoadingMessage();

    this.accountService.getUsersAndRoles().subscribe(
      (results: any) => {
        this.allUsers = results[0];
        this.allRoles = results[1];
        this.loadAssignments();
      },
      error => {
        this.isLoading = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage("Load Error", "Unable to retrieve users and roles from the server.", MessageSeverity.Error, error);
      });
  }

  private loadAssignments() {
    this.accountService.getPasswordPolicyAssignments().subscribe(
      (assignments: PasswordPolicyAssignment[]) => {
        this.assignments = assignments;
        this.isLoading = false;
        this.alertService.stopLoadingMessage();
      },
      error => {
        this.isLoading = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage("Load Error", "Unable to retrieve current password policy assignments.", MessageSeverity.Error, error);
      });
  }

  get selectedUserCount() {
    return Object.keys(this.selectedUserIds).filter(id => this.selectedUserIds[id]).length;
  }

  get selectedRoleCount() {
    return Object.keys(this.selectedRoleIds).filter(id => this.selectedRoleIds[id]).length;
  }

  activate() {
    if (!this.targetAllUsers && this.selectedUserCount === 0 && this.selectedRoleCount === 0) {
      this.alertService.showMessage("Nothing selected", "Select at least one user or role, or choose \"All users\".", MessageSeverity.Warn);
      return;
    }

    if (this.requiredInDays <= 0 || this.repeatIntervalDays <= 0) {
      this.alertService.showMessage("Invalid values", "Both day values must be greater than 0.", MessageSeverity.Warn);
      return;
    }

    const request = {
      userIds: this.targetAllUsers ? [] : Object.keys(this.selectedUserIds).filter(id => this.selectedUserIds[id]).map(id => +id),
      roleIds: this.targetAllUsers ? [] : Object.keys(this.selectedRoleIds).filter(id => this.selectedRoleIds[id]).map(id => +id),
      allUsers: this.targetAllUsers,
      requiredInDays: this.requiredInDays,
      repeatIntervalDays: this.repeatIntervalDays
    };

    this.alertService.showDialog(
      `This will require ${this.targetAllUsers ? 'every user' : 'the selected users/roles'} to change their password every ${this.repeatIntervalDays} day(s), with a warning banner starting ${this.requiredInDays} day(s) before each deadline. Continue?`,
      Confirm,
      () => {
        this.isSaving = true;
        this.alertService.startLoadingMessage("Applying policy...");

        this.accountService.assignPasswordPolicy(request).subscribe(
          (result: any) => {
            this.isSaving = false;
            this.alertService.stopLoadingMessage();
            this.alertService.showMessage("Success", `Password policy applied to ${result.assignedCount} user(s).`, MessageSeverity.Success);
            this.selectedUserIds = {};
            this.selectedRoleIds = {};
            this.targetAllUsers = false;
            this.loadAssignments();

            // Covers assigning a policy to yourself while already logged in - that alone
            // doesn't fire a login/logout transition, so the banner would otherwise only
            // pick it up on its next periodic poll (up to 60s later).
            this.authService.passwordPolicyChanged.next();
          },
          error => {
            this.isSaving = false;
            this.alertService.stopLoadingMessage();
            this.alertService.showStickyMessage("Error", "Unable to apply the password policy.", MessageSeverity.Error, error);
          });
      });
  }

  deactivate(assignment: PasswordPolicyAssignment) {
    this.alertService.showDialog(
      `Stop enforcing a forced password change for "${assignment.userName}"?`,
      Confirm,
      () => {
        this.accountService.deactivatePasswordPolicy(assignment.userId).subscribe(
          () => {
            this.alertService.showMessage("Success", "Policy removed for this user.", MessageSeverity.Success);
            this.loadAssignments();
            this.authService.passwordPolicyChanged.next();
          },
          error => {
            this.alertService.showStickyMessage("Error", "Unable to remove the policy for this user.", MessageSeverity.Error, error);
          });
      });
  }
}
